import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { EyeIcon } from 'lucide-react'

import { Button, EMPTY_VALUE, Skeleton } from '@repo/ui'
import { ProductSelectField } from 'components/input'
import { useProducts } from 'hooks/api/useProducts'
import type { Product } from 'types'

/**
 * Which entity can fill a given prop path, and which of that entity's fields.
 *
 * `null` for "no entity fills this" — the common case, and the one that matters:
 * a template with no product path gets NO product picker, rather than a control
 * that writes nowhere. Read off the template, never assumed.
 */
type SampleEntity = 'product' | 'owner'
type SampleField = 'title' | 'sku' | 'image' | 'name' | 'email' | 'avatar'
interface SampleSlot {
  path: string
  entity: SampleEntity
  field: SampleField
}

/**
 * `product.title` -> segment `title`; `productTitle` -> `Title`. The prefix test is
 * ANCHORED rather than a substring search, which is load-bearing: `customer.title`
 * is a job title and must not be written with a product name.
 */
const splitEntityPath = (path: string): { entity: string; key: string } | null => {
  const segment = path.split('.').pop() ?? ''
  const dotted = /^([a-z]+)\./i.exec(path)
  if (dotted) return { entity: dotted[1].toLowerCase(), key: segment.toLowerCase() }
  const camel = /^([a-z]+)([A-Z]\w*)$/.exec(segment)
  if (camel) return { entity: camel[1].toLowerCase(), key: camel[2].toLowerCase() }
  return null
}

/**
 * The entity vocabulary, derived from what the registry's `sampleProps` ACTUALLY
 * use (measured 2026-09-05 across `packages/emails/src/registry.ts`): `product`
 * appears in 17 templates and `customer` in 5, plus one camel-cased `ownerName`.
 *
 * Both entities are still MATCHED. Only `product` is still PICKED — see the
 * "Why the owner is fixed" section on the component below. The owner match is
 * what lets the fixed sample find the slots it has to fill.
 *
 * Deliberately NOT mapped:
 *
 *  - `actor` (14 templates) is whoever performed the action — the person who
 *    scanned the item, or the admin who started a transfer. It is not the
 *    recipient, so filling it from an owner value would put the wrong person's
 *    name in the sentence. It needs its own treatment if it ever earns one.
 *  - `certificate` — there is NO `certificate` object in any template's
 *    `sampleProps`, so a certificate picker would have nothing to write to. That
 *    is exactly the control-that-writes-nowhere this file refuses to build. If a
 *    template later grows one, add it here and the picker appears by itself.
 *  - `company` (16 templates) is the sending brand, already supplied by the
 *    Studio theme + logo the preview is FOR. Letting it be picked would let the
 *    preview misrepresent which brand is sending.
 */
const ENTITY_ALIASES: Record<string, SampleEntity> = {
  product: 'product',
  customer: 'owner',
  owner: 'owner',
}

const FIELD_ALIASES: Record<SampleEntity, Record<string, SampleField>> = {
  product: {
    title: 'title',
    name: 'title',
    sku: 'sku',
    imglink: 'image',
    imgurl: 'image',
    image: 'image',
    imageurl: 'image',
  },
  owner: {
    name: 'name',
    fullname: 'name',
    firstname: 'name',
    email: 'email',
    avatar: 'avatar',
    avatarimglink: 'avatar',
  },
}

const slotForPath = (path: string): SampleSlot | null => {
  const split = splitEntityPath(path)
  if (!split) return null
  const entity = ENTITY_ALIASES[split.entity]
  if (!entity) return null
  const field = FIELD_ALIASES[entity][split.key]
  return field ? { path, entity, field } : null
}

/**
 * The ONE owner every email preview is rendered against. Not a picked record, and
 * not a plausible-looking stand-in for one.
 *
 * ## It has to READ as a sample, and that is the first requirement, not a nicety
 *
 * A "Jane Smith" in an email preview is indistinguishable from a real customer,
 * so an operator can reasonably believe they are looking at a message addressed
 * to someone in their list — or worse, one about to be sent. The leading token is
 * therefore the literal word "Sample", which is read before anything else in a
 * greeting line, and the address is on `example.com` (RFC 2606, reserved and
 * permanently undeliverable) so it cannot be mistaken for a live address even if
 * the name is skimmed.
 *
 * ## The rest of it is deliberately awkward, and that is the POINT
 *
 * This is the advantage a fixed sample has over a randomly-picked real customer:
 * whoever happens to be first in a brand's list is usually a short ASCII name
 * that stresses nothing. Every part of this one was chosen to fail loudly:
 *
 *  - **Long** (38 characters). Long enough to wrap the 600px email container's
 *    heading and to run past a single line in a greeting, which is where a
 *    fixed-height header or a `white-space: nowrap` shows up.
 *  - **Outside Latin-1, not merely accented.** `Ș` (U+0218) and `ă` (U+0103) sit
 *    beyond the Latin-1 block, so they exercise the actual UTF-8 path rather than
 *    the single-byte one an accented `í` alone would still survive. `Þ` (U+00DE)
 *    and the acute vowels cover the ordinary accented case alongside them.
 *  - **Two hyphenated compounds and a mixed-case interior.** These are what break
 *    naive initial-taking and title-casing, both of which the email layer does.
 *
 * ## `avatar` is undefined on purpose — with an honest caveat about what that proves
 *
 * No avatar means an owner rendered as a mark falls to its initials rather than an
 * image, which is the branch worth previewing because it is the one live sends
 * take. **But be precise about the scope of that claim here:** the initials plate
 * belongs to `EmailActor`, which renders the ACTOR, and `actor` is deliberately
 * unmapped above. No owner-bearing template in the registry declares a
 * `customer.avatar` path today, so this field is currently unreachable and the
 * initials path is NOT exercised by the owner slot. It is set explicitly so that a
 * template which later grows one inherits the no-image branch rather than a
 * fabricated photo.
 */
const SAMPLE_OWNER = {
  name: 'Sample Ana-María Ștefănescu-Þórsdóttir',
  email: 'sample.owner@example.com',
  avatar: undefined,
} as const

const sampleOwnerValue = (field: SampleField): string | undefined => {
  switch (field) {
    case 'name':
      return SAMPLE_OWNER.name
    case 'email':
      return SAMPLE_OWNER.email
    case 'avatar':
      return SAMPLE_OWNER.avatar
    default:
      return undefined
  }
}

/** The property NAME shown against each owner value in the list. */
const OWNER_FIELD_LABELS: Partial<Record<SampleField, string>> = {
  name: 'Owner name',
  email: 'Owner email',
  avatar: 'Owner avatar',
}

/**
 * The label column's width, in px.
 *
 * 88, and it is derived rather than picked. Measured in the panel's own font
 * (13px Lato) with a rendered probe, after `document.fonts` reported ready:
 * the two labels this list can show are `Owner name` at 74.9px and
 * `Owner email` at 74.6px, so 88 clears the wider one by 13px.
 *
 * The other side of the same budget is what makes the width matter. The panel
 * measures 352px at 1280x800, so the value cell gets 352 - 88 - 12 (the gap) =
 * 252px, and `SAMPLE_OWNER.name` renders at 235.6px — one line, with 16px to
 * spare. A wider label column would wrap the sample name, which is the one
 * value in this panel chosen specifically to be long (see `SAMPLE_OWNER`).
 *
 * Wrapping is not a failure here, just a cost: the value cell is
 * `minmax(0, 1fr)` and wraps rather than clipping or scrolling, which is what
 * a narrower window or a longer sample gets. Re-measure if a third label is
 * added — 13px is slack, not immunity (§49's reasoning, applied to a label
 * column rather than a table header).
 */
const PROPERTY_LABEL_W = 88

/**
 * One `name -> value` row: the property name in a fixed left column, its result
 * next to it.
 *
 * ## Why this is not `DetailSummaryRow` (§28/§47 pre-flight, done and recorded)
 *
 * `components/display/DetailField` already exports the app's two label/value
 * primitives, and `DetailSummaryRow` is the label-left/value-right one. It was
 * measured against this panel and rejected on two counts, both structural
 * rather than taste:
 *
 *  - **It is `space-between`, so values do not share a column.** Its own
 *    docstring scopes it to "a short scalar that reads better aligned in a
 *    second column", and these are not short scalars: the sample owner's name
 *    is 235.6px against the email's 164.4px, so the two rows would start their
 *    values 71px apart and the pair would read as ragged rather than as a list.
 *    That is the exact failure `InfoRow`'s `columns` prop was added to fix, one
 *    component over.
 *  - **Its type scale is a step up from this panel's.** It renders `body3`/`body2`
 *    (14px label, 16px value) for money rows on full-width invoice pages. This
 *    panel is 352px wide and set at 13px throughout, so a 16px value would be
 *    the largest text in it.
 *
 * So this is the FIRST hand-roll of a dense inline label/value row, which §36
 * allows. **The next one is not:** if a second surface needs this, hoist it into
 * `components/display/DetailField` alongside `DetailField` and
 * `DetailSummaryRow` rather than copying it, since that module is already the
 * home for exactly this family.
 */
const PropertyRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      // `minmax(0, 1fr)` rather than `1fr` on the value track: a bare `1fr` is
      // `minmax(auto, 1fr)`, whose `auto` floor is the content's min-content
      // width, so one long unbroken value (an email address) would push the
      // track past its share and shove the label column out of alignment.
      gridTemplateColumns: `${PROPERTY_LABEL_W}px minmax(0, 1fr)`,
      columnGap: 3,
      alignItems: 'baseline',
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.5 }}>{label}</Typography>
    {typeof children === 'string' ? (
      // `anywhere`, not `break-word`: an email address has no break opportunity
      // its whole length, so `break-word` would let it overflow the track before
      // breaking. This is the §74 no-horizontal-scroll rule applied to a cell.
      <Typography sx={{ fontSize: 13, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    ) : (
      children
    )}
  </Box>
)

/**
 * Flatten `{a: {b: 1}}` to `[{path: 'a.b', value: 1}]`.
 *
 * ⚠️ This and `setByPath` below mirror the two helpers inlined in
 * `LabsPage/sections/NotificationsLab.tsx`. They are duplicated rather than
 * shared ONLY because Labs is owned by another workstream right now; by §36 this
 * is the second hand-roll and they should be hoisted into one module the next
 * time Labs is touched. Behaviour is identical — keep them that way until then.
 */
const isPlainObject = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

const flattenProps = (
  obj: Record<string, any>,
  prefix = '',
): { path: string; value: any }[] =>
  Object.entries(obj).flatMap(([k, v]) => {
    const path = prefix ? `${prefix}.${k}` : k
    return isPlainObject(v) ? flattenProps(v, path) : [{ path, value: v }]
  })

const setByPath = (
  obj: Record<string, any>,
  path: string,
  value: any,
): Record<string, any> => {
  const [head, ...rest] = path.split('.')
  if (!rest.length) return { ...obj, [head]: value }
  return {
    ...obj,
    [head]: setByPath(isPlainObject(obj[head]) ? obj[head] : {}, rest.join('.'), value),
  }
}

const productValue = (product: Product, field: SampleField): string | undefined => {
  switch (field) {
    case 'title':
      return product.title ?? undefined
    case 'sku':
      return (product as any).sku ?? undefined
    case 'image':
      // `images` is `(string | null)[]` — coerce a null first entry to undefined so
      // the template renders its no-image branch instead of an `src=""`.
      return (product as any).images?.[0] || undefined
    default:
      return undefined
  }
}

interface EmailSampleDataProps {
  /**
   * Selected template id. Not rendered — it is the reset key. This panel owns
   * "start over when the template changes" outright, because the alternative
   * (the parent clearing while this component seeds) is two effects writing the
   * same state in one commit. See the reset effect below.
   */
  templateId?: string
  /** The selected template's demo content — the shape the picker is derived FROM. */
  sampleProps?: Record<string, any>
  /** The live props object, or undefined when the template needs none. */
  value?: Record<string, any>
  onChange: (next: Record<string, any> | undefined) => void
}

/**
 * VRG-560 — fill the Studio email preview with the brand's OWN products, against a
 * fixed sample owner.
 *
 * ## This is a PREVIEW control, and that has to be unmistakable
 *
 * The panel sits in the preview pane, under the rendered email — not in the
 * editor column with the Save-gated cards — and says in as many words that it
 * changes nothing but the preview. That separation is the whole design risk: a
 * brand that reads this as configuration would believe it had chosen which
 * product its customers get emailed about.
 *
 * Mechanically it cannot participate in Save. This state is local `useState`
 * held by `EmailPreview`, never a react-hook-form field, and StudioPage's
 * `hasChanges` compares only `EDITABLE_STYLE_KEYS` off `watch()` — so picking a
 * sample cannot dirty the form even by accident (§54).
 *
 * ## Why the OWNER is fixed and the PRODUCT is picked
 *
 * This surface used to offer both as pickers, each reading the brand's real
 * records. The owner picker is gone, and the asymmetry is deliberate rather than
 * a scope cut:
 *
 *  - **An owner is a person.** The picker put a live, searchable list of the
 *    brand's customers — real names against real email addresses — inside a
 *    DESIGN tool. Studio access is plausibly held by a designer or an outside
 *    agency with no business seeing the customer list at all, and a theme editor
 *    is not a surface anyone would think to check when auditing who can read
 *    customer data. So the exposure is both unnecessary and easy to miss.
 *  - **A product earns its place because product PRESENTATION varies.** A long
 *    title, a missing photo, an odd aspect ratio, an empty SKU — those are real
 *    layout risks the brand's own catalogue can surface and a made-up product
 *    cannot. That is a genuine reason to read live records.
 *  - **An owner is just a name and an address.** A real one tells a brand nothing
 *    a sample does not, and a deliberately awkward sample tells it MORE (see
 *    `SAMPLE_OWNER` above). There is no preview question that only a real
 *    customer can answer.
 *
 * There is consequently NO request for customer data from this surface: no
 * `useOwnersQuery`, no `/owners` call, no owner list held in state.
 *
 * ## Scoping (products)
 *
 * The product list comes from a `@CompanyId()`-derived endpoint
 * (`/products/admin/company`), where the server reads the company off the auth
 * token and ignores any client-supplied id. There is no code path here that can
 * name another company's records. The Labs equivalent takes a `companyId` in the
 * URL and is PlatformAdmin-gated; that endpoint is deliberately NOT used here.
 *
 * ## Reuse
 *
 * `ProductSelectField` is the shared picker (§28/§47), so this file authors no
 * popover of its own — the floating-surface chrome (§69) is `SearchSelect`'s,
 * already correct, rather than a hand-rolled paper.
 */
export const EmailSampleData = ({
  templateId,
  sampleProps,
  value,
  onChange,
}: EmailSampleDataProps) => {
  const theme = useTheme()
  const [product, setProduct] = useState<Product | null>(null)

  // Derived from the LIVE props so it stays correct as values are written, but the
  // shape only ever comes from the template — a pick never adds or removes a slot.
  const slots = useMemo(() => {
    const source = value ?? sampleProps
    if (!source) return [] as SampleSlot[]
    return flattenProps(source)
      .map(({ path }) => slotForPath(path))
      .filter((s): s is SampleSlot => !!s)
  }, [value, sampleProps])

  const productSlots = useMemo(() => slots.filter(s => s.entity === 'product'), [slots])
  const ownerSlots = useMemo(() => slots.filter(s => s.entity === 'owner'), [slots])

  /**
   * The owner properties to LIST, derived from the slots this template actually
   * declares — deduplicated by field, because two paths can resolve to the same
   * one and the list would otherwise repeat a row.
   *
   * ⚠️ This is a behaviour change, not just a re-layout, and it is a correctness
   * fix. The panel used to render the sample name AND the sample email for any
   * template with any owner slot at all. Most owner-bearing templates declare
   * only one of the two — `customer-transfer-confirmation-code`'s `sampleProps`
   * is `customer: { name: 'Jordan' }` and names no address — so the panel was
   * telling the operator the preview used `sample.owner@example.com` on a
   * template that never renders an address. A property list that names a value
   * the render does not use is the same defect as a control that writes nowhere,
   * which this file already refuses to build.
   *
   * A field with no label is dropped rather than shown under its raw key: the
   * map is the vocabulary, so an unlabelled field is one nobody has decided how
   * to name yet.
   */
  const ownerRows = useMemo(() => {
    const seen = new Set<SampleField>()
    return ownerSlots.flatMap(({ field }) => {
      if (seen.has(field)) return []
      seen.add(field)
      const label = OWNER_FIELD_LABELS[field]
      return label ? [{ field, label }] : []
    })
  }, [ownerSlots])

  /**
   * The template's own `sampleProps` with every owner slot overwritten by
   * `SAMPLE_OWNER`, or `undefined` when the template has no owner slot at all.
   *
   * `undefined` is the load-bearing half: for a template with no owner, the panel
   * sends NO `props` and the render stays byte-identical to the one before this
   * panel existed. Only templates that actually name an owner pay for a payload.
   *
   * Built from `sampleProps` rather than from `value`, so it is the fixed starting
   * point for BOTH the initial fill and Reset, and neither can be contaminated by
   * a pick made in between.
   */
  const ownerFilledBase = useMemo(() => {
    if (!sampleProps) return undefined
    const owners = flattenProps(sampleProps)
      .map(({ path }) => slotForPath(path))
      .filter((s): s is SampleSlot => !!s && s.entity === 'owner')
    if (!owners.length) return undefined
    return owners.reduce<Record<string, any>>(
      (accumulated, slot) => setByPath(accumulated, slot.path, sampleOwnerValue(slot.field)),
      sampleProps,
    )
  }, [sampleProps])

  /**
   * Reset to the template's opening state: no product pick, the fixed sample
   * owner seeded into whatever owner slots this template has.
   *
   * ⚠️ Keyed on `templateId` through a ref, and BOTH halves of that matter.
   *
   * Keyed on the id rather than on `ownerFilledBase` because `sampleProps` comes
   * from a query, so its object identity changes on any background refetch. An
   * effect keyed on the derived object would re-seed on a refetch and silently
   * discard the operator's product pick mid-session.
   *
   * Owned HERE rather than in `EmailPreview` because the parent used to clear its
   * override state on the same `templateId` change. With a seed to perform, that
   * is two effects writing one piece of state in a single commit: React runs the
   * child's first, so the parent's clear landed last and wiped the seed, and the
   * panel only recovered on a follow-up render — at the cost of a wasted preview
   * render request every time a template was opened. One owner, no race.
   */
  const seededFor = useRef<string | undefined | symbol>(Symbol('unseeded'))
  useEffect(() => {
    if (seededFor.current === templateId) return
    seededFor.current = templateId
    setProduct(null)
    onChange(ownerFilledBase)
  }, [templateId, ownerFilledBase, onChange])

  // Only ask the server for a list this template can actually use.
  const { data: productsResponse, isLoading: productsLoading } = useProducts(
    { limit: 100 },
    { enabled: productSlots.length > 0 },
  )

  const products = (productsResponse?.data ?? []) as Product[]

  const handleProduct = (picked: Product | null) => {
    setProduct(picked)
    if (!picked) return
    const base = value ?? ownerFilledBase ?? sampleProps ?? {}
    onChange(
      productSlots.reduce(
        (accumulated, slot) => setByPath(accumulated, slot.path, productValue(picked, slot.field)),
        base,
      ),
    )
  }

  const handleReset = () => {
    setProduct(null)
    // Back to the state this template opens in: the fixed sample owner, and nothing
    // else. `undefined` for an owner-less template, which sends no `props` at all so
    // the render is byte-identical to the one before this panel was ever touched.
    onChange(ownerFilledBase)
  }

  if (!sampleProps) return null

  // `ownerRows`, not `ownerSlots`: the rows are what actually renders, so gating on
  // the slots would let an owner slot with no label reach the "there is something to
  // show" branch and then show nothing — an empty block under a heading, which is
  // the state the else-branch copy exists to prevent.
  const hasSlots = productSlots.length > 0 || ownerRows.length > 0

  return (
    <Box
      sx={{
        // Separated from the email above by a rule rather than a card. The pane's
        // "wall" follows the light/dark toggle, so a card here would need its own
        // background in both appearances and would read as part of the message.
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        pt: 5,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={4}
      >
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EyeIcon size={15} strokeWidth={1.5} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>
              Preview properties
            </Typography>
          </Stack>
          {/* Separate sentences, not em-dash-joined clauses (§15). This copy is the
              only thing standing between "preview control" and "setting", so it says
              both halves plainly: what it does, and what it does not do. */}
          <Typography sx={{ fontSize: 13, lineHeight: 1.5 }} color="text.secondary">
            Swap in one of your own products to see how this email looks with real
            content. This only changes the preview. Nothing here is saved, and no
            email is sent.
          </Typography>
        </Stack>
        {product && (
          <Button variant="secondary" disableElevation onClick={handleReset}>
            Reset
          </Button>
        )}
      </Stack>

      {hasSlots ? (
        <Stack spacing={4} sx={{ mt: 5 }}>
          {productSlots.length > 0 &&
            (productsLoading ? (
              // Footprint parity (§19): the label bar plus the 49px field the picker
              // resolves to, in the same place, so nothing shifts when it arrives.
              <Stack spacing={2}>
                <Skeleton variant="rounded" sx={{ width: 96, height: 14 }} />
                <Skeleton variant="rounded" sx={{ width: '100%', height: 49 }} />
              </Stack>
            ) : (
              <ProductSelectField
                label="Sample product"
                products={products}
                value={product}
                onChange={handleProduct}
              />
            ))}

          {/* Read-only, and shown rather than hidden. The operator is looking at a
              person's name inside their own email; saying WHOSE it is, right there,
              is what stops it being read as a customer of theirs. */}
          {ownerRows.length > 0 && (
            <Stack spacing={2}>
              {ownerRows.map(({ field, label }) => (
                <PropertyRow key={field} label={label}>
                  {sampleOwnerValue(field) ?? EMPTY_VALUE}
                </PropertyRow>
              ))}
              <Typography
                sx={{ fontSize: 13, lineHeight: 1.5 }}
                color="text.secondary"
              >
                This owner is a fixed sample, so previews never use a real person's
                details. The awkward name is on purpose. It shows how the layout
                copes with a long one.
              </Typography>
            </Stack>
          )}
        </Stack>
      ) : (
        // Says why there is nothing to change, rather than rendering an empty block.
        // Most templates in this list are codes and confirmations that name no
        // product and no person, so this is the common case, not an error.
        <Typography
          sx={{ fontSize: 13, lineHeight: 1.5, mt: 4 }}
          color="text.secondary"
        >
          This email doesn't include a product or an owner, so there's nothing to swap in.
        </Typography>
      )}
    </Box>
  )
}
